import React, { useEffect, useState } from 'react';
import './ResetPassword.scss';
import Card from 'components/Layout/Card';
import { Formik } from 'formik';
import * as Yup from 'yup';
import PasswordInput from 'components/form/PasswordInput';
import Button from 'components/form/Button';
import HeaderLayout from 'components/Layout/HeaderLayout';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { decrypt, objectToFormData } from 'utils/helpers';
import { useDispatch } from 'react-redux';
import {
  forgotPassword,
  setApiError,
  showSuccess,
  verifyForgotPasswordLink,
} from 'store/slices';
import Loader from 'components/Layout/Loader';

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [tokenData, setTokenData] = useState({});
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifyFailed(true);
        setLoading(false);
        return;
      }

      try {
        const safeToken = token.replace(/\s/g, '+');
        const decrypted = decrypt(token);
        const params = new URLSearchParams(decrypted);
        const email = params.get('email');
        const time = params.get('time');
        setTokenData({ email, time });
        const formData = objectToFormData({
          token: safeToken,
        });
        const response = await dispatch(verifyForgotPasswordLink(formData));

        if (response?.status === 200) {
          setUserId(response?.data?.id);
          setVerifyFailed(false);
          dispatch(showSuccess(response?.message));
        } else {
          setVerifyFailed(true);
        }
      } catch (error) {
        console.error('Failed to decrypt or verify token:', error);
        setVerifyFailed(true);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, dispatch]);

  const initialValues = {
    email: tokenData.email || '',
    new_password: '',
    confirm_password: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string(),
    new_password: Yup.string()
      .required('New password is required.')
      .min(6, 'Password must be at least 6 characters.')
      .max(16, 'Password must be at most 16 characters.')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{6,16}$/,
        'Password must be 6-16 characters and include uppercase, lowercase, number, and special character.'
      ),
      // .matches(
      //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,16}$/,
      //   'Password must be 6-16 characters and include uppercase, lowercase, number, and special character.'
      // ),
    confirm_password: Yup.string()
      .required('Confirm password is required.')
      .oneOf([Yup.ref('new_password'), null], 'Passwords do not match.'),
  });

  const handleChangePassword = async (values) => {
    setSubmitLoading(true);

    const formData = objectToFormData({
      type: 3,
      id: userId,
      ...values,
    });

    const response = await dispatch(forgotPassword(formData));

    if (response?.status === 200) {
      dispatch(
        setApiError({
          show: true,
          message: 'Password changed successfully.',
          type: 'success',
        })
      );
      setTimeout(() => {
        navigate(`/${params.type}/login`);
      }, 2000);
    }

    setSubmitLoading(false);
  };

  if (loading) {
    return (
      <HeaderLayout>
        <div className="loader-wrapper center-flex py-5">
          <Loader />
        </div>
      </HeaderLayout>
    );
  }

  return (
    <HeaderLayout>
      <div id="reset-password-container">
        <Card className="card-padding ps-5 pe-5 pt-5 pb-5">
          {!verifyFailed && (
            <div className="d-flex center-flex position-relative mb-5">
              <div className="text-28-500 color-black-olive">
                Change Password
              </div>
            </div>
          )}

          {verifyFailed ? (
            <div>
              <p className="color-red text-center cpb-20">
                Token has expired. Please request a new link.
              </p>
              <Button
                text="Back"
                btnStyle="light-outline"
                className="h-35 cps-20 cpe-20 w-35"
                onClick={() => navigate(`/${params?.type}/forgot-password`)}
              />
            </div>
          ) : (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleChangePassword}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                handleBlur,
              }) => (
                <form
                  onSubmit={handleSubmit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                >
                  <div className="cmb-30 pt-3">
                    <PasswordInput
                      labelClass="mb-2"
                      id="new_password"
                      label="New Password"
                      placeholder="Enter New Password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.new_password}
                      error={touched.new_password && errors.new_password}
                      isShowSuggetion
                    />
                  </div>
                  <div>
                    <PasswordInput
                      labelClass="mb-2"
                      id="confirm_password"
                      label="Confirm New Password"
                      placeholder="Confirm New Password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.confirm_password}
                      error={
                        touched.confirm_password && errors.confirm_password
                      }
                    />
                  </div>

                  <div className="mt-5">
                    <Button
                      isRounded
                      text="Submit"
                      btnStyle="primary-dark"
                      onClick={handleSubmit}
                      disabled={submitLoading}
                      btnLoading={submitLoading}
                    />
                  </div>
                </form>
              )}
            </Formik>
          )}
        </Card>
      </div>
    </HeaderLayout>
  );
};

export default ResetPassword;
